import { action, observable } from 'mobx'
import { OCRScopingJobSidePanelViewModel } from '@/features/jobs/scoping/OCRScopingJobSidePanelViewModel'
import { type OCRScopeWorkStore } from '@/features/jobs/scoping/OCRScopeWorkStore'
import { type Member } from '@/features/iam/members/Member'
import { type Observable } from 'rxjs'
import { CreateOCRScopeJobsViewModel } from '@/features/jobs/scoping/components/CreateOCRScopeJobsDialog/CreateOCRScopeJobsViewModel'
import { type PublishedLegendStore } from '@/features/jobs/legends/PublishedLegendStore'
import type {
  ListTeamMembersFn,
  GetCurrentMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  SearchMembersFn,
  ListTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { NavigateFn } from '@/RootStore'
import { OCRScopingJobsPermissionViewModel } from './OCRScopingJobsPermissionViewModel'
import { OCRScopingJobMainPanelViewModel } from '@/features/jobs/scoping/OCRScopingJobMainPanelViewModel'
import { CreateJobDialogViewModel } from '@/features/jobs/drafting/components/CreateJobDialog/CreateJobDialogViewModel'
import { type JobCreationRootViewModel } from '@/features/jobs/drafting/JobCreationRootViewModel'
import type {
  DownloadDocumentFn,
  SearchDocumentsFn,
} from '@/features/documents'
import { type DraftJobStore } from '@/features/jobs/drafting/DraftJobStore'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for scoping jobs.
 */
export class OCRScopingJobsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly permissionVm

  readonly mainPanelVm

  readonly sidePanelVm

  readonly createScopeJobsVm

  /**
   * Whether the side panel is visible.
   */
  @observable accessor sidePanelVisible = true

  constructor(
    getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    ocrScopeWorkStore: OCRScopeWorkStore,
    draftJobStore: DraftJobStore,
    publishedLegendStore: PublishedLegendStore,
    searchMembers: SearchMembersFn,
    searchDocuments: SearchDocumentsFn,
    downloadDocument: DownloadDocumentFn,
    currentMember$: Observable<Member>,
    getCurrentMember: GetCurrentMemberFn,
    findTeamMembersByUserPublicId: ListTeamMembersForWorkspaceMemberFn,
    listTeamMembers: ListTeamMembersFn,
    newAlert: AlertFactory,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    createAlertRetryWrapper: AlertRetryWrapperFactory,
    navigate: NavigateFn,
    jobCreationRootVm: JobCreationRootViewModel,
  ) {
    this.permissionVm = new OCRScopingJobsPermissionViewModel(
      getCurrentUserWorkspaceMemberships,
    )

    this.mainPanelVm = new OCRScopingJobMainPanelViewModel(
      this.permissionVm,
      ocrScopeWorkStore,
      searchDocuments,
      downloadDocument,
      getCurrentMember,
      navigate,
      newAlert,
      createAlertErrorHandler,
      jobCreationRootVm,
      new CreateJobDialogViewModel(navigate, newAlert, jobCreationRootVm),
    )

    this.sidePanelVm = new OCRScopingJobSidePanelViewModel(
      this.permissionVm,
      ocrScopeWorkStore,
      draftJobStore,
      getCurrentUserWorkspaceMemberships,
      currentMember$,
      newAlert,
      createAlertRetryWrapper,
    )

    this.createScopeJobsVm = new CreateOCRScopeJobsViewModel(
      ocrScopeWorkStore,
      searchMembers,
      publishedLegendStore,
      getCurrentUserWorkspaceMemberships,
      currentMember$,
      getCurrentMember,
      findTeamMembersByUserPublicId,
      listTeamMembers,
      newAlert,
      createAlertErrorHandler,
      createAlertRetryWrapper,
    )

    this._lifecycle
      .manage(this.sidePanelVm)
      .manage(this.mainPanelVm)
      .manage(this.createScopeJobsVm)
  }

  @action.bound
  toggleSidePanel() {
    this.sidePanelVisible = !this.sidePanelVisible
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
